import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiService } from '../services/geminiService';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { geminiApi } from '../services/geminiApi';
import DeploymentOptions from '../components/DeploymentOptions';

export function SQLWriter() {
  const [queryInput, setQueryInput] = useState('');
  const [databaseType, setDatabaseType] = useState('postgresql');
  const [queryType, setQueryType] = useState('select');
  const [optimizationFocus, setOptimizationFocus] = useState('performance');
  const [generatedQuery, setGeneratedQuery] = useState('');
  const [queryExplanation, setQueryExplanation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDestructiveWarning, setShowDestructiveWarning] = useState(false);

  const databaseOptions = [
    { value: 'postgresql', label: 'PostgreSQL', description: 'Open-source, feature-rich database' },
    { value: 'mysql', label: 'MySQL', description: 'Popular open-source database' },
    { value: 'sqlserver', label: 'SQL Server', description: 'Microsoft enterprise database' },
    { value: 'oracle', label: 'Oracle', description: 'Enterprise-grade database system' },
    { value: 'databricks', label: 'Databricks', description: 'Unified analytics platform with SQL support' },
    { value: 'snowflake', label: 'Snowflake', description: 'Cloud data warehouse platform' }
  ];

  const queryTypeOptions = [
    { value: 'select', label: 'SELECT', description: 'Retrieve data from tables' },
    { value: 'insert', label: 'INSERT', description: 'Add new records' },
    { value: 'update', label: 'UPDATE', description: 'Modify existing records' },
    { value: 'delete', label: 'DELETE', description: 'Remove records', isDestructive: true },
    { value: 'join', label: 'JOIN', description: 'Combine data from multiple tables' },
    { value: 'subquery', label: 'Subquery', description: 'Nested query operations' },
    { value: 'window', label: 'Window', description: 'Advanced analytics functions' },
    { value: 'create', label: 'CREATE', description: 'Create new tables/objects', isDestructive: true },
    { value: 'drop', label: 'DROP', description: 'Remove tables/objects', isDestructive: true }
  ];

  const optimizationOptions = [
    { value: 'performance', label: 'Performance Optimized' },
    { value: 'readability', label: 'Readable Code' },
    { value: 'memory', label: 'Memory Efficient' },
    { value: 'indexed', label: 'Index Optimized' }
  ];

  const quickStartGuides = [
    {
      title: "For Beginners",
      description: "New to SQL? Start here to learn the basics of querying data.",
      keywords: ["Basic Queries", "Simple Joins", "Data Retrieval"],
      example: "Example: Show all customers who made a purchase in the last month",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      title: "For Database Developers",
      description: "Advanced query optimization and performance tuning.",
      keywords: ["Indexing", "Query Plans", "Performance"],
      example: "Example: Optimize a complex join with proper indexing strategy",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "For Data Analysts",
      description: "Focus on data analysis and reporting queries.",
      keywords: ["Aggregations", "Analytics", "Reporting"],
      example: "Example: Calculate monthly sales trends with year-over-year comparison",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setQueryInput(transcribedText);
  };

  const isDestructiveOperation = (type) => {
    return queryTypeOptions.find(opt => opt.value === type)?.isDestructive || false;
  };

  const parseGeneratedResponse = (response) => {
    // Split response into SQL and explanation sections
    const sections = response.split(/(?=Explanation:|Performance considerations:|Best practices:|Safety considerations:)/i);
    
    // First section should be the SQL query
    const sqlQuery = sections[0].trim();
    
    // Combine remaining sections into explanation
    const explanation = sections.slice(1).join('\n\n').trim();
    
    return { sqlQuery, explanation };
  };

  const generateQuery = async () => {
    if (!queryInput.trim()) {
      toast.error('Please provide query requirements');
      return;
    }

    if (isDestructiveOperation(queryType)) {
      setShowDestructiveWarning(true);
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Generate an optimized SQL query for: ${queryInput}
Database Type: ${databaseType}
Query Type: ${queryType}
Optimization Focus: ${optimizationFocus}

Please provide:
- The optimized SQL query (start with "SQL Query:" on a new line)
- Explanation of the query structure (start with "Explanation:" on a new line)
- Performance considerations (start with "Performance considerations:" on a new line)
- Any relevant indexes or optimizations
- Best practices applied (start with "Best practices:" on a new line)
- Safety considerations and warnings (start with "Safety considerations:" on a new line)

Format the response with clear sections and include comments in the SQL code.`;

      // Use geminiService for SQL generation
      const responseText = await geminiService.generateSQL(prompt, {
        databaseType,
        queryType,
        optimizationFocus
      });

      // Check if there's an error message in the response
      if (responseText && typeof responseText === 'string') {
        if (responseText.includes('Error generating SQL:')) {
          throw new Error(responseText);
        }
        
        const { sqlQuery, explanation } = parseGeneratedResponse(responseText);
        setGeneratedQuery(sqlQuery);
        setQueryExplanation(explanation);
        toast.success('Query generated successfully!');
      } else {
        throw new Error('No content received from the API');
      }
    } catch (error) {
      console.error('Error generating query:', error);
      toast.error(error.message || 'Failed to generate query. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-500/40 via-blue-300/10 to-blue-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-400/40 via-blue-300/10 to-blue-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-800/50 border border-blue-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                <span className="text-sm text-blue-100">AI-Powered SQL</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                SQL Writer
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-blue-200 max-w-xl">
                Generate optimized SQL queries with AI assistance
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple Databases</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Performance Optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Beginners learning SQL - Get help writing basic queries</li>
              <li>Database Developers - Optimize complex queries and improve performance</li>
              <li>Data Analysts - Generate analytical queries for reporting</li>
            </ul>
          </div>

          <div className="space-y-8">
            {/* Query Input with Example */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Query Requirements
              </label>
              <div className="mb-2">
                <div className="text-sm text-gray-500">Example: "Show all customers who made purchases over $1000 in the last 3 months, including their contact details and total spend"</div>
              </div>
              <textarea
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                rows="4"
                placeholder="Describe your query requirements in plain English..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Database Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Database Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {databaseOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDatabaseType(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      databaseType === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Query Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Query Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {queryTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setQueryType(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      queryType === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optimization Focus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optimization Focus
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {optimizationOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setOptimizationFocus(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      optimizationFocus === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateQuery}
              disabled={isGenerating}
              className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                isGenerating
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating Query...
                </div>
              ) : (
                'Generate Query'
              )}
            </button>

            {/* Destructive Operation Warning Modal */}
            {showDestructiveWarning && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl p-6 max-w-md">
                  <div className="flex items-center text-red-600 mb-4">
                    <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h3 className="text-lg font-medium">Warning: Destructive Operation</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    You are about to generate a query that could modify or delete data. Please ensure you:
                    <ul className="list-disc list-inside mt-2">
                      <li>Have proper backups</li>
                      <li>Understand the impact</li>
                      <li>Have necessary permissions</li>
                    </ul>
                  </p>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowDestructiveWarning(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowDestructiveWarning(false);
                        generateQuery();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Proceed Anyway
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Query Section */}
            {generatedQuery ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Generated SQL Query</h3>
                  <button
                    onClick={() => copyToClipboard(generatedQuery)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* SQL Query */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">SQL Query</h4>
                    <pre className="bg-gray-800 text-gray-100 rounded-lg p-4 overflow-x-auto">
                      <code>{generatedQuery}</code>
                    </pre>
                  </div>
                  
                  {/* Query Explanation */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Query Explanation</h4>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 overflow-y-auto max-h-[500px]">
                      <div className="markdown-content">
                        {queryExplanation.split('\n').map((line, index) => {
                          // Check if line is a section header
                          if (line.match(/^(Explanation|Performance considerations|Best practices|Safety considerations):/i)) {
                            return <h5 key={index} className="font-medium text-gray-900 mt-4 mb-2">{line}</h5>;
                          }
                          return <p key={index} className="text-gray-600 mb-2">{line}</p>;
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No query generated yet</h3>
                <p className="mt-1 text-sm text-gray-500">Start by describing your query requirements.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="sql-writer" position="none" />
    </div>
  );
}

export default SQLWriter; 